// Reusable ROUT form-validation kit.
// Field-level feedback: red outline + pale-yellow background + an inline plain-language
// message with an error icon. Plus a clickable "Please correct the following" summary that
// expands the owning section (if collapsed), scrolls the field into view (clear of sticky
// banners) and focuses it. Designed to be dropped into any ROUT form, dialog or inline editor.
import React, { useState, useRef, useCallback } from 'react';
import { AlertCircle, AlertTriangle } from 'lucide-react';

// Applied to an invalid input/select/trigger.
export const ERROR_FIELD_CLASS = 'border-red-500 ring-1 ring-red-400 bg-yellow-50 focus-visible:ring-red-500';

export function useFieldErrors() {
  const [errors, setErrorsState] = useState({}); // { fieldKey: message }
  const refs = useRef({});
  const expanders = useRef({});

  const register = useCallback((key) => (node) => {
    if (node) refs.current[key] = node;
  }, []);
  const registerExpander = useCallback((key, fn) => { expanders.current[key] = fn; }, []);

  const setErrors = useCallback((next) => setErrorsState(next || {}), []);
  const clearError = useCallback((key) => setErrorsState((e) => {
    if (!e || !e[key]) return e;
    const next = { ...e };
    delete next[key];
    return next;
  }), []);
  const clearAll = useCallback(() => setErrorsState({}), []);

  const jumpTo = useCallback((key) => {
    expanders.current[key]?.(); // expand collapsed section first
    // Let any expansion render, then scroll + focus (block:'center' keeps it clear of sticky banners).
    window.requestAnimationFrame(() => setTimeout(() => {
      const el = refs.current[key];
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = el.matches?.('input,textarea,select,button,[tabindex]')
        ? el
        : el.querySelector?.('input,textarea,select,button,[tabindex]');
      try { (focusable || el).focus?.({ preventScroll: true }); } catch (_) { /* noop */ }
    }, 60));
  }, []);

  return {
    errors,
    hasErrors: Object.keys(errors).length > 0,
    setErrors, clearError, clearAll,
    register, registerExpander, jumpTo,
    cls: (key) => (errors[key] ? ` ${ERROR_FIELD_CLASS}` : ''),
  };
}

// Inline per-field message shown directly beneath the field.
export function FieldError({ message, testid }) {
  if (!message) return null;
  return (
    <p role="alert" data-testid={testid || 'field-error'}
      className="mt-1.5 flex items-start gap-1.5 text-xs font-medium text-red-700">
      <AlertCircle size={13} className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </p>
  );
}

// "Please correct the following" summary — each item jumps to its field.
export function CorrectionSummary({ errors, labels = {}, onJump, title = 'Please correct the following' }) {
  const entries = Object.entries(errors || {});
  if (!entries.length) return null;
  return (
    <div role="alert" data-testid="correction-summary"
      className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 animate-in fade-in slide-in-from-top-1 duration-200">
      <div className="flex items-center gap-2 text-sm font-bold text-red-800">
        <AlertTriangle size={16} /> {title}
      </div>
      <ul className="mt-2 space-y-1">
        {entries.map(([key, msg]) => (
          <li key={key}>
            <button type="button" data-testid={`correction-item-${key}`}
              onClick={() => onJump?.(key)}
              className="text-left text-xs text-red-700 transition-colors hover:text-red-900 hover:underline">
              <span className="font-semibold">{labels[key] || key}:</span> {msg}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
